import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, FileText } from "lucide-react";

export default function ProfessionalProfile() {
    const [prof, setProf] = useState(null);
    const [professions, setProfessions] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/professionals/me").then(({ data }) => setProf(data.data));
        api.get("/professions").then(({ data }) => setProfessions(data.data || []));
    }, []);

    if (!prof) return <div>Caricamento…</div>;

    const update = (k, v) => setProf((p) => ({ ...p, [k]: v }));
    const updateAvail = (k, v) => setProf((p) => ({ ...p, availability: { ...(p.availability || {}), [k]: v } }));
    const updateArea = (k, v) => setProf((p) => ({ ...p, interest_area: { ...(p.interest_area || {}), [k]: v } }));

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                full_name: prof.full_name, phone: prof.phone, city: prof.city, province: prof.province,
                region: prof.region, profession: prof.profession, specializations: prof.specializations,
                albo_number: prof.albo_number, vat_number: prof.vat_number, years_experience: prof.years_experience,
                skills: prof.skills, bio: prof.bio, availability: prof.availability, interest_area: prof.interest_area,
                hourly_rate: prof.hourly_rate,
            };
            await api.put("/professionals/me", payload);
            toast.success("Profilo aggiornato");
        } catch (e) {
            toast.error("Errore salvataggio");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Il mio profilo professionale</h1>
                    <p className="text-sm text-[#5C5C5C] mt-1">Più completo è il profilo, migliori sono i match.</p>
                </div>
                {prof.verification_status === "VERIFIED" ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1" data-testid="verification-badge">
                        <ShieldCheck className="w-3.5 h-3.5" />Verificato
                    </Badge>
                ) : prof.verification_status === "UNDER_REVIEW" ? (
                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200 gap-1" data-testid="verification-badge">
                        <ShieldAlert className="w-3.5 h-3.5" />In verifica
                    </Badge>
                ) : (
                    <Badge variant="outline" className="gap-1" data-testid="verification-badge">
                        <ShieldAlert className="w-3.5 h-3.5" />Non verificato
                    </Badge>
                )}
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Foto profilo</h2>
                <FileUpload kind="photo" preview={prof.photo_url}
                    onUploaded={() => api.get("/professionals/me").then(({data}) => setProf(data.data))}
                    label="Carica foto" testId="upload-photo" />
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Informazioni personali</h2>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Nome completo</Label>
                        <Input value={prof.full_name || ""} onChange={(e) => update("full_name", e.target.value)} data-testid="pf-fullname" /></div>
                    <div><Label>Telefono</Label>
                        <Input value={prof.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="pf-phone" /></div>
                    <div><Label>Città</Label>
                        <Input value={prof.city || ""} onChange={(e) => update("city", e.target.value)} data-testid="pf-city" /></div>
                    <div><Label>Provincia</Label>
                        <Input value={prof.province || ""} onChange={(e) => update("province", e.target.value)} data-testid="pf-province" /></div>
                    <div><Label>Regione</Label>
                        <Input value={prof.region || ""} onChange={(e) => update("region", e.target.value)} data-testid="pf-region" /></div>
                </div>
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Informazioni professionali</h2>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Professione</Label>
                        <Select value={prof.profession || ""} onValueChange={(v) => update("profession", v)}>
                            <SelectTrigger data-testid="pf-profession"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                                {professions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Anni di esperienza</Label>
                        <Input type="number" min={0} value={prof.years_experience || 0}
                            onChange={(e) => update("years_experience", parseInt(e.target.value || 0))} data-testid="pf-years" /></div>
                    <div><Label>Numero albo</Label>
                        <Input value={prof.albo_number || ""} onChange={(e) => update("albo_number", e.target.value)} data-testid="pf-albo" /></div>
                    <div><Label>Partita IVA</Label>
                        <Input value={prof.vat_number || ""} onChange={(e) => update("vat_number", e.target.value)} data-testid="pf-vat" /></div>
                    <div><Label>Specializzazioni (virgola)</Label>
                        <Input value={(prof.specializations || []).join(", ")}
                            onChange={(e) => update("specializations", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                            data-testid="pf-specs" /></div>
                    <div><Label>Competenze (virgola)</Label>
                        <Input value={(prof.skills || []).join(", ")}
                            onChange={(e) => update("skills", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                            data-testid="pf-skills" /></div>
                    <div className="md:col-span-2"><Label>Chi sono</Label>
                        <Textarea rows={4} value={prof.bio || ""} onChange={(e) => update("bio", e.target.value)} data-testid="pf-bio" /></div>
                </div>
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Disponibilità</h2>
                <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Stato</Label>
                        <Select value={prof.availability?.status || "available"} onValueChange={(v) => updateAvail("status", v)}>
                            <SelectTrigger data-testid="pf-avail-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="available">Disponibile</SelectItem>
                                <SelectItem value="partial">Parziale</SelectItem>
                                <SelectItem value="unavailable">Non disponibile</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Zona di interesse</h2>
                <div className="grid md:grid-cols-3 gap-4">
                    <div><Label>Città</Label>
                        <Input value={prof.interest_area?.city || ""} onChange={(e) => updateArea("city", e.target.value)} data-testid="pf-area-city" /></div>
                    <div><Label>Provincia</Label>
                        <Input value={prof.interest_area?.province || ""} onChange={(e) => updateArea("province", e.target.value)} data-testid="pf-area-prov" /></div>
                    <div><Label>Raggio (km)</Label>
                        <Input type="number" value={prof.interest_area?.radius_km || 50}
                            onChange={(e) => updateArea("radius_km", parseInt(e.target.value || 0))} data-testid="pf-area-radius" /></div>
                </div>
            </div>

            <div className="hl-card p-6 space-y-4">
                <h2 className="font-display font-bold text-lg">Documenti e certificati</h2>
                <p className="text-sm text-[#5C5C5C]">Carica iscrizione albo, certificazioni o corsi. Un amministratore verificherà il profilo.</p>
                <FileUpload kind="certificate"
                    onUploaded={() => api.get("/professionals/me").then(({data}) => setProf(data.data))}
                    label="Carica documento" testId="upload-cert" />
                <div className="space-y-2">
                    {(prof.documents || []).map((d) => (
                        <div key={d.id} className="flex items-center justify-between p-3 bg-[#F9F9F7] rounded-md" data-testid={`doc-${d.id}`}>
                            <div className="flex items-center gap-3">
                                <FileText className="w-4 h-4 text-[#1C3F39]" />
                                <div>
                                    <div className="text-sm font-medium">{d.filename}</div>
                                    <div className="text-xs text-[#5C5C5C]">{new Date(d.uploaded_at).toLocaleDateString("it-IT")}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={busy} className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="save-profile-btn">
                    {busy ? "Salvataggio…" : "Salva profilo"}
                </Button>
            </div>
        </div>
    );
}
